import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User } from '../types';
import api from '../services/api';
import createRipple from '../utils/ripple';
import './LoginScreen.css';

interface LoginScreenProps {
  onLogin: (user: User) => void;
  onShowNotification: (message: string, type: 'error' | 'success') => void;
}

/* Слайд текста внутри кнопки: старый уезжает вниз, новый приезжает сверху
   (при обратном переключении — зеркально), направление задаёт custom */
const submitLabelVariants = {
  enter: (dir: number) => ({ y: dir * -26, opacity: 0 }),
  center: { y: 0, opacity: 1 },
  exit: (dir: number) => ({ y: dir * 26, opacity: 0 })
};

/* Спринг для переключателя и layout-анимаций */
const pillSpring = { type: 'spring', duration: 0.45, bounce: 0.15 } as const;

/* Сильная ease-in-out кривая для раскрытия/схлопывания */
const easeInOut = [0.77, 0, 0.175, 1] as const;

/* Каскадное появление экрана */
const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.5, ease: [0.23, 1, 0.32, 1] as const }
  }
};

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin, onShowNotification }) => {
  const [isLogin, setIsLogin] = useState(true);
  // Ручной вход (логин/пароль, регистрация) доступен только по прямому адресу
  // /login — на главной показываем лишь логотип и вход через Telegram
  const manualLogin = window.location.pathname.startsWith('/login');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    note: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Валидация через наши тоасты, без браузерных подсказок
    if (!formData.username.trim() || !formData.password.trim()) {
      onShowNotification('Введите имя пользователя и пароль', 'error');
      return;
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        const response = await api.login({
          username: formData.username,
          password: formData.password
        });

        if (response.success && response.data) {
          onLogin(response.data.user);
          onShowNotification('Вход выполнен успешно', 'success');
        } else {
          onShowNotification(response.message || 'Ошибка при входе', 'error');
        }
      } else {
        if (formData.password !== formData.confirmPassword) {
          onShowNotification('Пароли не совпадают', 'error');
          return;
        }

        if (!formData.username.trim() || !formData.password.trim() || !formData.note.trim()) {
          onShowNotification('Заполните все обязательные поля', 'error');
          return;
        }

        const response = await api.register({
          username: formData.username,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          note: formData.note
        });

        if (response.success) {
          onShowNotification(response.message || 'Регистрация успешна. Ожидайте одобрения администратором.', 'success');
          setIsLogin(true);
          setFormData(prev => ({ ...prev, username: '', password: '', confirmPassword: '', note: '' }));
        } else {
          onShowNotification(response.message || 'Ошибка при регистрации', 'error');
        }
      }
    } catch (error: any) {
      onShowNotification(error.message || 'Произошла ошибка', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleTelegramLogin = () => {
    const botUrl = 'https://t.me/HiddenArmyInbox_bot?start=auth';
    window.open(botUrl, '_blank');
    onShowNotification('Бот автоматически отправит вам ссылку для входа', 'success');
  };

  const submitLabel = isLoading
    ? (isLogin ? 'Вход…' : 'Регистрация…')
    : (isLogin ? 'Войти' : 'Зарегистрироваться');

  return (
    <div className="login-screen">
      <div className="lg-glow" aria-hidden="true" />

      <motion.div
        className={`lg-content ${manualLogin ? '' : 'minimal'}`}
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {/* Шапка */}
        <motion.header className="lg-header" variants={itemVariants}>
          <motion.div
            className="lg-logo-halo"
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <img src="/logo-hidden.png" alt="" className="lg-logo-mark" />
          </motion.div>
          <h1 className="lg-logo-title">
            Hidden <span className="lg-logo-title-accent">Inbox</span>
          </h1>
        </motion.header>

        {/* Переключатель Вход / Регистрация — только при ручном входе */}
        {manualLogin && (
        <motion.div className="lg-switch" role="tablist" variants={itemVariants}>
          {([
            { mode: true, icon: 'fa-sign-in-alt', label: 'Вход' },
            { mode: false, icon: 'fa-user-plus', label: 'Регистрация' }
          ] as const).map(({ mode, icon, label }) => (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={isLogin === mode}
              className={`lg-switch-btn ${isLogin === mode ? 'active' : ''}`}
              onClick={() => setIsLogin(mode)}
            >
              {isLogin === mode && (
                <motion.span
                  className="lg-switch-pill"
                  layoutId="lg-switch-pill"
                  transition={pillSpring}
                />
              )}
              <span className="lg-switch-label">
                <i className={`fas ${icon}`}></i>
                {label}
              </span>
            </button>
          ))}
        </motion.div>
        )}

        {/* Форма */}
        <motion.form className="lg-form" onSubmit={handleSubmit} variants={itemVariants} noValidate>
          {manualLogin && (<>
          <div className="lg-field">
            <i className="fas fa-user lg-field-icon"></i>
            <input
              type="text"
              name="username"
              placeholder="Имя пользователя"
              value={formData.username}
              onChange={handleInputChange}
              required
              autoComplete="username"
              autoFocus
            />
          </div>

          <div className="lg-field">
            <i className="fas fa-lock lg-field-icon"></i>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Пароль"
              value={formData.password}
              onChange={handleInputChange}
              required
              autoComplete={isLogin ? 'current-password' : 'new-password'}
            />
            <button
              type="button"
              className="lg-eye"
              onClick={() => setShowPassword(prev => !prev)}
              aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              tabIndex={-1}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.i
                  key={showPassword ? 'hide' : 'show'}
                  className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.7 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                />
              </AnimatePresence>
            </button>
          </div>

          {/* Дополнительные поля регистрации */}
          <AnimatePresence initial={false}>
            {!isLogin && (
              <motion.div
                key="extra-fields"
                className="lg-extra"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.4, ease: easeInOut },
                  opacity: { duration: 0.25, ease: 'easeOut' }
                }}
              >
                <div className="lg-extra-inner">
                  <div className="lg-field">
                    <i className="fas fa-lock lg-field-icon"></i>
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder="Подтвердите пароль"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required={!isLogin}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="lg-field">
                    <i className="fas fa-sticky-note lg-field-icon"></i>
                    <input
                      type="text"
                      name="note"
                      placeholder="Заметка (Фармер 1.1, МБ 2.1 и т.д.)"
                      value={formData.note}
                      onChange={handleInputChange}
                      required={!isLogin}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Основная кнопка */}
          <motion.button
            type="submit"
            className="lg-submit"
            disabled={isLoading}
            whileTap={{ scale: 0.985 }}
            onPointerDown={createRipple}
          >
            <AnimatePresence mode="popLayout" initial={false} custom={isLogin ? -1 : 1}>
              <motion.span
                key={submitLabel}
                className="lg-submit-label"
                custom={isLogin ? -1 : 1}
                variants={submitLabelVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1], delay: 0.15 }}
              >
                {isLoading ? (
                  <span className="lg-spinner" aria-hidden="true" />
                ) : (
                  <i className={`fas ${isLogin ? 'fa-sign-in-alt' : 'fa-user-plus'}`}></i>
                )}
                {submitLabel}
              </motion.span>
            </AnimatePresence>
          </motion.button>
          </>)}

          {/* Кнопка Telegram — только в режиме входа */}
          <AnimatePresence initial={false}>
            {isLogin && (
              <motion.div
                key="tg"
                className="lg-tg-wrap"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.4, ease: easeInOut },
                  opacity: { duration: 0.25, ease: 'easeOut' }
                }}
              >
                <motion.button
                  type="button"
                  className="lg-telegram"
                  onClick={handleTelegramLogin}
                  disabled={isLoading}
                  whileTap={{ scale: 0.985 }}
                  onPointerDown={createRipple}
                >
                  <i className="fab fa-telegram"></i>
                  Войти через Телеграм
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>
      </motion.div>
    </div>
  );
};

export default LoginScreen;
